import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRemoveUser, useUpdateCurrentUser } from 'services/user'
import { useUserContext } from 'context/user-context'
import { useFormFields } from 'hooks/useFormFields'
import { Card } from 'components/Card'
import { Heading } from 'components/Heading'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { Alert } from 'components/Alert'
import { Flex } from 'components/Flex'
import { Dialog } from 'components/Dialog'
import styles from './AccountForm.module.scss'

export const AccountForm: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [currentUser] = useUserContext()

  const [updateUser, { status: updateStatus }] = useUpdateCurrentUser()
  const [removeUser, { status: deleteStatus }] = useRemoveUser(currentUser?.id || '')

  const [confirmAction, setConfirmAction] = useState(false)
  const [form, onFieldChange] = useFormFields({
    firstName: currentUser?.firstName,
    lastName: currentUser?.lastName,
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    updateUser(form)
  }

  const handleRemoveUser = () => {
    removeUser(undefined, {
      onSuccess: () => {
        setTimeout(() => navigate('/'), 500)
      },
    })
  }

  return (
    <Card>
      <Stack space="large">
        <Stack>
          <Stack space="small">
            <Heading size="card">{t('account.my_account.heading')}</Heading>
            <Text>{t('account.my_account.subtitle')}</Text>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Stack space="medium">
              <TextInput
                required
                type="text"
                name="firstName"
                label={t('account.my_account.form.name')}
                value={form.firstName}
                onChange={onFieldChange}
              />

              <TextInput
                required
                type="text"
                name="lastName"
                label={t('account.my_account.form.surname')}
                value={form.lastName}
                onChange={onFieldChange}
              />

              <TextInput
                disabled
                type="email"
                name="email"
                label={t('account.my_account.form.email')}
                value={currentUser?.email}
              />

              <Button type="submit" loading={updateStatus === 'loading'}>
                {t('account.my_account.form.cta')}
              </Button>
              {updateStatus === 'success' && (
                <Alert tone="positive" full>
                  {t('common.messages.success')}
                </Alert>
              )}
              {updateStatus === 'error' && (
                <Alert tone="critical" full>
                  {t('common.messages.error')}
                </Alert>
              )}
            </Stack>
          </form>
        </Stack>
        <div className={styles.closeAccountBorder}></div>
        <Flex justify="between" align="center">
          <div>
            <Heading size="widget">{t('account.close.title')}</Heading>
            <Text>{t('account.close.desc')}</Text>
          </div>
          <Button variant="danger" onClick={() => setConfirmAction(true)}>
            {t('account.close.cta')}
          </Button>
        </Flex>
      </Stack>

      <Dialog
        isOpen={confirmAction}
        onClose={() => setConfirmAction(false)}
        aria-label={t('account.close.dialog.title')}
      >
        <Stack space="large">
          <div>
            <Heading size="secondary">{t('account.close.dialog.title')}</Heading>
            <Text>{t('account.close.dialog.desc')}</Text>
          </div>
          <Stack space="medium">
            <Button full onClick={() => setConfirmAction(false)} variant="secondary">
              {t('account.close.dialog.cancel')}
            </Button>
            <Button
              full
              onClick={() => handleRemoveUser()}
              variant="danger"
              loading={deleteStatus === 'loading'}
            >
              {t('account.close.dialog.cta')}
            </Button>

            {deleteStatus === 'success' && (
              <Alert tone="positive" full>
                {t('common.messages.success')}
              </Alert>
            )}

            {deleteStatus === 'error' && (
              <Alert tone="critical" full>
                {t('common.messages.error')}
              </Alert>
            )}
          </Stack>
        </Stack>
      </Dialog>
    </Card>
  )
}

AccountForm.displayName = 'AccountForm'
